import { FormEvent, useCallback, useEffect, useState } from 'react';
import { api, UsageReport } from '../api';
import { Icon } from '../Icon';

/**
 * Who is using the system, and how much, between two times.
 *
 * Time is time inside tests, measured by the server — see UsageService for the
 * rule. It loads only when asked (no reload per keystroke) because every view
 * is written to the audit log, and a search box that logged each letter would
 * bury the entries that matter.
 *
 * Administrators only. Counts and durations, never scores: how much someone
 * practises is an operating question, how well they do is theirs.
 */

const DAY = 86_400_000;

/** `datetime-local` wants local wall-clock time without a zone. */
function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function duration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

type Sort = 'minutes' | 'attempts' | 'answers' | 'recent' | 'name';

export function AdminUsage({ onBack }: { onBack: () => void }) {
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 7 * DAY)));
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [minMinutes, setMinMinutes] = useState('');
  const [maxMinutes, setMaxMinutes] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('minutes');
  const [report, setReport] = useState<UsageReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (range?: { from: string; to: string }) => {
    const f = range?.from ?? from, t = range?.to ?? to;
    if (Number.isNaN(new Date(f).getTime()) || Number.isNaN(new Date(t).getTime())) {
      setErr('Enter a start and an end time.'); return;
    }
    setErr(null); setBusy(true);
    try {
      const params = new URLSearchParams({
        from: new Date(f).toISOString(), to: new Date(t).toISOString(), sort,
      });
      if (minMinutes.trim()) params.set('minMinutes', minMinutes.trim());
      if (maxMinutes.trim()) params.set('maxMinutes', maxMinutes.trim());
      if (q.trim()) params.set('q', q.trim());
      setReport(await api.get<UsageReport>(`/admin/usage?${params}`));
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }, [from, to, minMinutes, maxMinutes, q, sort]);

  // First view only; after that the administrator decides when to look again.
  useEffect(() => { void load(); }, []);

  function submit(e: FormEvent) { e.preventDefault(); if (!busy) void load(); }

  /** Quick ranges ending now; they set the fields too, so what is shown is what was asked. */
  function lastDays(days: number) {
    const now = new Date();
    const start = days === 0
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : new Date(now.getTime() - days * DAY);
    const range = { from: toLocalInput(start), to: toLocalInput(now) };
    setFrom(range.from); setTo(range.to);
    void load(range);
  }

  const field = {
    width: '100%', font: 'inherit', padding: '0.5rem', minHeight: '2.75rem',
    border: '1px solid var(--border-strong)', borderRadius: '8px',
    background: 'var(--surface)', color: 'var(--ink)',
  } as const;
  const cell = { display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: '11rem', flex: '1 1 11rem' } as const;

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Usage</h1>
      <p className="muted">
        Who used the system between two times, and how much. <strong>Time in tests</strong> runs
        from when a test starts, through each saved answer, to when it is submitted, as recorded
        by the server. A pause between two of those is counted only up
        to {report?.idleCapMinutes ?? 30} minutes, and nothing is counted after a test's deadline,
        so a test left open overnight does not read as hours of study. Time spent outside a test
        is not measured.
      </p>
      <p className="muted">
        Each time you view this report, it is recorded in the audit log with the filters you used.
      </p>

      <form onSubmit={submit} className="card" aria-label="Filter usage">
        <div className="btn-row" style={{ alignItems: 'flex-end' }}>
          <span style={cell}>
            <label htmlFor="us-from">From</label>
            <input id="us-from" type="datetime-local" required value={from}
                   onChange={(e) => setFrom(e.target.value)} style={field} />
          </span>
          <span style={cell}>
            <label htmlFor="us-to">To</label>
            <input id="us-to" type="datetime-local" required value={to}
                   onChange={(e) => setTo(e.target.value)} style={field} />
          </span>
        </div>
        <div className="btn-row" style={{ marginTop: '0.75rem' }} role="group" aria-label="Quick ranges">
          <button type="button" className="btn-ghost" onClick={() => lastDays(0)}>Today</button>
          <button type="button" className="btn-ghost" onClick={() => lastDays(7)}>Last 7 days</button>
          <button type="button" className="btn-ghost" onClick={() => lastDays(30)}>Last 30 days</button>
        </div>

        <div className="btn-row" style={{ marginTop: '0.75rem', alignItems: 'flex-end' }}>
          <span style={cell}>
            <label htmlFor="us-min">Time in tests at least (min)</label>
            <input id="us-min" type="number" min={0} inputMode="numeric" value={minMinutes}
                   onChange={(e) => setMinMinutes(e.target.value)} style={field} />
          </span>
          <span style={cell}>
            <label htmlFor="us-max">Time in tests at most (min)</label>
            <input id="us-max" type="number" min={0} inputMode="numeric" value={maxMinutes}
                   onChange={(e) => setMaxMinutes(e.target.value)} style={field} />
          </span>
          <span style={cell}>
            <label htmlFor="us-q">Name or email</label>
            <input id="us-q" value={q} onChange={(e) => setQ(e.target.value)} style={field} />
          </span>
          <span style={cell}>
            <label htmlFor="us-sort">Sort by</label>
            <select id="us-sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={field}>
              <option value="minutes">Most time in tests</option>
              <option value="attempts">Most tests</option>
              <option value="answers">Most answers</option>
              <option value="recent">Most recently active</option>
              <option value="name">Name</option>
            </select>
          </span>
        </div>

        <div className="btn-row" style={{ marginTop: '1rem' }}>
          {/* aria-disabled, not disabled: a disabled button drops keyboard focus to the page. */}
          <button className="btn-primary" type="submit" aria-disabled={busy}>
            {busy ? 'Loading…' : 'Show usage'}
          </button>
        </div>
      </form>

      {err && <div className="notice notice--warn" role="alert">{err}</div>}

      {/* Mounted empty, then filled: a live region inserted already full is often not read out. */}
      <p role="status" aria-busy={busy} style={{ marginTop: '1rem' }}>
        {report && (
          <>
            <strong>{report.totals.activeUsers}</strong>
            {` of ${report.totals.accounts} account${report.totals.accounts === 1 ? '' : 's'} took a test in this window`}
            {` · ${duration(report.totals.minutesInTests)} in tests`}
            {` · ${report.totals.attempts} test${report.totals.attempts === 1 ? '' : 's'}`}
            {` · ${report.totals.answers} question${report.totals.answers === 1 ? '' : 's'} answered`}
          </>
        )}
      </p>

      {report && (
        <>

          {report.users.length === 0 ? (
            <p className="muted">Nobody matches these filters in this window.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="board">
                <caption className="visually-hidden">
                  Usage from {when(report.from)} to {when(report.to)}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col">Time in tests</th>
                    <th scope="col">Tests (submitted)</th>
                    <th scope="col">Questions answered</th>
                    <th scope="col">Last active in window</th>
                    <th scope="col">Last sign-in</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.users.map((u) => (
                    <tr key={u.id}>
                      <th scope="row" style={{ fontWeight: 'normal', textAlign: 'left', textTransform: 'none', letterSpacing: 'normal', fontSize: 'inherit', color: 'inherit' }}>
                        <strong>{u.displayName ?? '—'}</strong>
                        <br /><span className="muted">{u.email}</span>
                      </th>
                      <td>{duration(u.minutesInTests)}</td>
                      <td>{u.attempts} ({u.submitted})</td>
                      <td>{u.answers}</td>
                      <td>{when(u.lastActivityAt)}</td>
                      <td>{when(u.lastLoginAt)}</td>
                      <td>{u.suspended ? <strong>Suspended</strong> : 'Active'}{u.role !== 'learner' ? ` · ${u.role}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
